import { dynamicFormApiConnector } from 'apiConnector/form/dynamic';
import * as apiErrorHelper from 'epic/apiErrorEpicHelper';
import { tabRemoved } from 'epic/workspace/common/tabs/tabsEpics';
import type { DynamicFormResult } from 'models/forms/DynamicFormResult';
import { ofType } from 'redux-observable';
import type { Observable, SchedulerLike } from 'rxjs';
import { of, throwError } from 'rxjs';
import { catchError, filter, map, mergeMap, takeUntil, timeoutWith, withLatestFrom } from 'rxjs/operators';
import { dynamicFormEnrichmentReceivedThunk } from 'state/dynamic/dynamicForm.thunks';
import { commonFormActions } from 'state/forms/actions/form';
import type { EpicWithThunk } from 'state/state.common';

export interface DynamicProductCodesRequest {
  tabId?: string;
  negotiationId?: string;
}

export interface DynamicRequestProductCodesEpicApi {
  generateProductCodes(request: DynamicProductCodesRequest): Observable<DynamicFormResult>;
}

export function requestProductCodesEpic(timeoutInMillis: number = 60000, appScheduler?: SchedulerLike): EpicWithThunk {
  return (action$, state$, { oldHttpClient, frontLogger, selectors: { getDynamicFormInfos } }) =>
    action$.pipe(
      ofType('PRODUCT_CODES_REQUESTED'),
      filter(action => action.workspace === 'dynamic'),
      withLatestFrom(state$),
      mergeMap(([{ tabId }, state]) => {
        const { negotiationId } = getDynamicFormInfos(state, tabId)!;

        const request: DynamicProductCodesRequest = {
          tabId,
          negotiationId,
        };

        const tabRemoved$ = tabRemoved(action$, tabId, state$);

        return dynamicFormApiConnector(oldHttpClient)
          .generateProductCodes(request)
          .pipe(
            takeUntil(tabRemoved$),
            map(
              ({
                values,
                blocksMetadata,
                fieldsMetadata,
                productDescription,
                version: newVersion,
                actions,
                quoteQuery,
                errors,
                allowedRequests,
              }) =>
                dynamicFormEnrichmentReceivedThunk(
                  tabId,
                  blocksMetadata,
                  fieldsMetadata,
                  productDescription,
                  values,
                  newVersion,
                  actions,
                  quoteQuery,
                  allowedRequests,
                  errors,
                ),
            ),
            timeoutWith(timeoutInMillis, throwError(new Error('timeout')), appScheduler),
            catchError(error => {
              if (apiErrorHelper.isHttpError(error) && error.status === 400) {
                return of(commonFormActions.productCodesRejected('dynamic', tabId, error.response.rejections));
              }

              frontLogger.error(
                'Enrichment failure detected on dynamic screen. Session linked to negotiation id {negotiationId} will be rollbacked {@FrontError}',
                negotiationId,
                error,
              );

              return of(commonFormActions.fetchFormEnrichmentFailed('dynamic', tabId, error));
            }),
          );
      }),
    );
}
